import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
	SelectListCustom,
	SelectListNext,
	SelectListOption,
} from '@components';
import {
	ReflectionService,
	ReflectionServiceCreateLogResponse,
} from '@services/member';
import {
	DomainService,
	LanguageService,
	MAIN_KEY_PREFIX,
	MainKey,
	Skill,
	StorageService,
	TempKey,
	VolatileKey,
} from '@services/public';
import { APIErrors, isAPIErrors } from '@util';
import { ExposedPromise } from 'jakapa-utilities-lite';
import { ReplaySubject, Subscription } from 'rxjs';
import { ReflectionPage, ReflectionData } from '../common';

const PAGE_PATH: string = 'skillBuilders.reflection.pages.action';

@Component({
	selector: 'app-member-skill-builder-reflection-action',
	templateUrl: './action.component.html',
	styleUrls: ['./action.component.scss'],
})
export class MemberSkillBuilderReflectionActionComponent
	implements OnInit, OnDestroy
{
	@Input() data: ReflectionData;
	options: ReplaySubject<Array<SelectListOption>> = new ReplaySubject<
		Array<SelectListOption>
	>(1);
	defaultOptions: Array<SelectListOption> = [];
	customOptions: Array<SelectListOption> = [];
	custom: SelectListCustom = {
		add: this.addCustom.bind(this),
		delete: this.deleteCustom.bind(this),
	};
	next: SelectListNext = {
		display: null,
		onClick: this.nextOnClick.bind(this),
	};
	defaultLoaded: ExposedPromise<void> = new ExposedPromise<void>();
	languageLoaded: ExposedPromise<void> = new ExposedPromise<void>();
	private _subscriptions: Subscription = new Subscription();

	// Page langauge.
	page: { [key: string]: string } = {
		title: '',
		subTitle: '',
		submit: '',
	};

	constructor(
		private _domainSvc: DomainService,
		private _languageSvc: LanguageService,
		private _reflectionSvc: ReflectionService,
		private _router: Router,
		private _storageSvc: StorageService,
	) {}

	ngOnInit(): void {
		// Get page language.
		this._languageSvc
			.get([PAGE_PATH], {
				[PAGE_PATH]: {
					domain: this._domainSvc
						.getDomain(this.data.domainUID)
						.name.toLowerCase(),
					skill: this._domainSvc
						.getSkill(this.data.skillUID)
						.name.toLowerCase(),
				},
			})
			.then((value) => {
				if (typeof value[PAGE_PATH] === 'object' && value[PAGE_PATH] !== null)
					this.page = value[PAGE_PATH];
				this.languageLoaded.resolve();
			});

		// Get options list.
		const skill: Skill = this._domainSvc.getSkill(this.data.skillUID);
		const path: string = `domains2.${skill?.domain.uid}.skills.${skill.uid}.goalActions`;
		this._languageSvc.get([path]).then((value) => {
			if (typeof value[path] === 'object' && value[path] !== null)
				this.defaultOptions = this._languageSvc.objectToOrderedArray(
					value[path],
				);
			this.defaultLoaded.resolve();
		});
		this._subscriptions.add(
			this._storageSvc.updates.subscribe((update) => {
				if (update.key === MAIN_KEY_PREFIX + MainKey.ALL) {
					this.customOptions = [];
					if (Array.isArray(update.value[MainKey.GOAL_ACTIONS])) {
						for (const option of update.value[MainKey.GOAL_ACTIONS]) {
							if (typeof option !== 'string') continue;
							this.customOptions.push({ display: option, custom: true });
						}
					}
					this._mergeAndSelectOptions();
				}
				if (update.key === MAIN_KEY_PREFIX + MainKey.CLEAR) {
					this.customOptions = [];
					this._mergeAndSelectOptions();
				}
				if (update.key === MAIN_KEY_PREFIX + MainKey.GOAL_ACTIONS) {
					this.customOptions = [];
					if (Array.isArray(update.value)) {
						for (const option of update.value) {
							if (typeof option !== 'string') continue;
							this.customOptions.push({ display: option, custom: true });
						}
					}
					this._mergeAndSelectOptions();
				}
			}),
		);
		let options: Array<string> = this._storageSvc.getMainStorage(
			MainKey.GOAL_ACTIONS,
		);
		if (Array.isArray(options)) {
			for (const option of options) {
				if (typeof option !== 'string') continue;
				this.customOptions.push({ display: option, custom: true });
			}
		}

		this._mergeAndSelectOptions();
	}

	private async _mergeAndSelectOptions(): Promise<void> {
		await this.defaultLoaded;
		await this.languageLoaded;
		this.options.next(
			[...this.customOptions, ...this.defaultOptions].map(
				(option: SelectListOption) => {
					if (option.display === this.data.action) {
						option.selected = true;
						if (!option.value?.skillBuilderType) this.next.display = null;
						else this.next.display = this.page.submit;
					}
					return option;
				},
			),
		);
	}

	addCustom(customOption: string): void {
		if (
			this.customOptions.findIndex(
				(option) => option.display === customOption,
			) !== -1
		)
			return;
		if (
			this.defaultOptions.findIndex(
				(option) => option.display === customOption,
			) !== -1
		)
			return;
		this.customOptions.push({ display: customOption, custom: true });
		this._storageSvc.setMainStorage(
			MainKey.GOAL_ACTIONS,
			this.customOptions.map((option) => option.display),
		);
	}

	deleteCustom(option: SelectListOption): void {
		this.customOptions = this.customOptions.filter(
			(customOption) => customOption.display !== option.display,
		);
		if (this.customOptions.length === 0)
			this._storageSvc.setMainStorage(MainKey.GOAL_ACTIONS, null);
		else
			this._storageSvc.setMainStorage(
				MainKey.GOAL_ACTIONS,
				this.customOptions.map((option) => option.display),
			);
	}

	onChange(option: SelectListOption): void {
		if (!option.value?.skillBuilderType) this.next.display = null;
		else this.next.display = this.page.submit;
	}

	nextOnClick(option: SelectListOption): void {
		if (!option.value?.skillBuilderType) {
			this._storageSvc.setTempStorage(TempKey.REFLECTION, {
				...this.data,
				page: ReflectionPage.SUMMARY,
				action: option.display,
				skillBuilderType: null,
			});
			return;
		}

		this.data.action = option.display;
		this.data.skillBuilderType = option.value.skillBuilderType;
		this._reflectionSvc
			.createLog(this.data)
			.subscribe((res: ReflectionServiceCreateLogResponse | APIErrors) => {
				if (!isAPIErrors(res)) {
					this._storageSvc.setVolatileStorage(
						VolatileKey.GOAL_PLAN_UUID,
						res.goalPlanUUID,
					);
					this._router.navigate(['/member/skill-builder/goal']);
				}
			});
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
	}
}
